import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { Page } from '../../components/Layout'
import { ErrorBox, Field, TextInput } from '../../components/ui'
import { Login } from '../../components/Login'
import { useAuth } from '../../hooks/useAuth'
import { supabase } from '../../lib/supabase'
import { apiPost } from '../../lib/api'
import { brl, dataBR, statusLabel } from '../../lib/format'

interface CampoFato {
  valor: string
  documento_fonte: string
  confianca: 'alta' | 'media' | 'baixa' | 'ausente'
  citacao: string
}
interface FatosCaso {
  fatos: (Record<string, CampoFato> & { observacoes?: string }) | null
  docs_lidos?: string[]
  docs_ignorados?: string[]
  erro?: string | null
  extraido_em?: string
}
interface Caso {
  id: string
  status: string
  empreendimento: string | null
  construtora: string | null
  valor_entrada: number | null
  valor_parcela: number | null
  percentual_exito: number | null
  processo_numero: string | null
  clientes: { nome: string; email: string | null } | null
}

const FATO_LABELS: [string, string][] = [
  ['unidade', 'Unidade'],
  ['matricula', 'Matrícula'],
  ['coautores', 'Co-autores'],
  ['cnpj_construtora', 'CNPJ construtora'],
  ['total_pago', 'Total pago'],
  ['num_parcelas_pagas', 'Parcelas pagas'],
  ['valor_financiado', 'Valor financiado'],
]
const CONF_COR: Record<string, string> = {
  alta: 'bg-green-100 text-green-800',
  media: 'bg-amber-100 text-amber-800',
  baixa: 'bg-orange-100 text-orange-800',
  ausente: 'bg-pfa-navy/10 text-pfa-slate',
}
interface Documento {
  id: string
  tipo: string
  status: string
  storage_path: string | null
  nome_arquivo: string | null
  mime_type: string | null
}
interface Pagamento { id: string; tipo: string; valor: number; status: string; vencimento: string | null }
interface Evento { id: string; tipo: string | null; descricao: string | null; data_evento: string }
interface Peticao { id: string; conteudo_md: string | null; status: string; versao: number; modelo_ia: string | null }

const TIPOS_EVENTO = [
  'liminar_deferida',
  'liminar_indeferida',
  'liminar_revogada',
  'sentenca',
  'valor_recebido',
  'outro',
]

export default function CasoDetalhe() {
  const { id } = useParams<{ id: string }>()
  const { user, loading } = useAuth()
  const [staff, setStaff] = useState<boolean | null>(null)
  const [caso, setCaso] = useState<Caso | null>(null)
  const [docs, setDocs] = useState<Documento[]>([])
  const [pagamentos, setPagamentos] = useState<Pagamento[]>([])
  const [eventos, setEventos] = useState<Evento[]>([])
  const [peticao, setPeticao] = useState<Peticao | null>(null)
  const [fatos, setFatos] = useState<FatosCaso | null>(null)
  const [erro, setErro] = useState<string | null>(null)
  const [busy, setBusy] = useState<string | null>(null)

  // edição da peça + protocolo
  const [conteudo, setConteudo] = useState('')
  const [dirty, setDirty] = useState(false)
  const [processoNumero, setProcessoNumero] = useState('')
  // registro de evento
  const [tipoEvento, setTipoEvento] = useState('liminar_deferida')
  const [descEvento, setDescEvento] = useState('')
  // visualizador de documentos (modal popup)
  const [viewer, setViewer] = useState<{ nome: string; url: string; kind: 'image' | 'pdf' | 'outro' } | null>(null)
  const [docBusy, setDocBusy] = useState<string | null>(null)

  async function carregar() {
    if (!id) return
    const { data: c, error } = await supabase
      .from('casos')
      .select(
        'id, status, empreendimento, construtora, valor_entrada, valor_parcela, percentual_exito, processo_numero, clientes(nome, email)',
      )
      .eq('id', id)
      .maybeSingle()
    if (error) {
      setErro(error.message)
      return
    }
    setCaso(c as unknown as Caso)
    if (c?.processo_numero) setProcessoNumero(c.processo_numero)
    const [{ data: d }, { data: p }, { data: ev }, { data: pet }, { data: cf }] = await Promise.all([
      supabase.from('documentos').select('id, tipo, status, storage_path, nome_arquivo, mime_type').eq('caso_id', id),
      supabase.from('pagamentos').select('id, tipo, valor, status, vencimento').eq('caso_id', id),
      supabase.from('eventos_processo').select('id, tipo, descricao, data_evento').eq('caso_id', id).order('data_evento', { ascending: false }),
      supabase.from('peticoes').select('id, conteudo_md, status, versao, modelo_ia').eq('caso_id', id).order('versao', { ascending: false }).limit(1),
      supabase.from('caso_fatos').select('fatos_jsonb').eq('caso_id', id).maybeSingle(),
    ])
    setDocs((d as Documento[]) ?? [])
    setPagamentos((p as Pagamento[]) ?? [])
    setEventos((ev as Evento[]) ?? [])
    const pt = (pet as Peticao[])?.[0] ?? null
    setPeticao(pt)
    setFatos(((cf as { fatos_jsonb: FatosCaso } | null)?.fatos_jsonb as FatosCaso) ?? null)
  }

  useEffect(() => {
    if (loading || !user) return
    ;(async () => {
      const { data: perfil } = await supabase.from('perfis').select('papel').eq('user_id', user.id).maybeSingle()
      const isStaff = perfil?.papel === 'staff'
      setStaff(isStaff)
      if (isStaff) await carregar()
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, user, id])

  // sincroniza a textarea SÓ quando a identidade da peça muda (não a cada recarregamento),
  // para não descartar edição em andamento ao registrar evento etc.
  useEffect(() => {
    if (peticao) {
      setConteudo(peticao.conteudo_md ?? '')
      setDirty(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [peticao?.id, peticao?.versao])

  // fecha o modal de documento com Esc
  useEffect(() => {
    if (!viewer) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setViewer(null)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [viewer])

  async function acao(nome: string, fn: () => Promise<void>) {
    setErro(null)
    setBusy(nome)
    try {
      await fn()
      await carregar()
    } catch (e) {
      setErro((e as Error).message)
    } finally {
      setBusy(null)
    }
  }

  function classificaArquivo(mime: string | null, nome: string | null): 'image' | 'pdf' | 'outro' {
    const m = (mime ?? '').toLowerCase()
    if (m.startsWith('image/')) return 'image'
    if (m === 'application/pdf') return 'pdf'
    const n = (nome ?? '').toLowerCase()
    if (/\.(png|jpe?g|gif|webp|bmp|heic|heif)$/.test(n)) return 'image'
    if (n.endsWith('.pdf')) return 'pdf'
    return 'outro'
  }

  // gera signed URL (staff tem RLS de leitura no bucket) e abre no modal
  async function abrirDoc(d: Documento) {
    if (!d.storage_path) return
    setErro(null)
    setDocBusy(d.id)
    const { data, error } = await supabase.storage.from('documentos-casos').createSignedUrl(d.storage_path, 600)
    setDocBusy(null)
    if (error || !data) {
      setErro(`Não foi possível abrir o documento: ${error?.message ?? 'erro'}`)
      return
    }
    setViewer({ nome: d.nome_arquivo ?? d.tipo, url: data.signedUrl, kind: classificaArquivo(d.mime_type, d.nome_arquivo) })
  }

  if (loading) return <Page><p className="text-pfa-slate">Carregando…</p></Page>
  if (!user) return <Page><Login staff /></Page>
  if (staff === false) return <Page><div className="card"><p className="text-pfa-slate">Área restrita à equipe.</p></div></Page>
  if (!caso) return <Page><ErrorBox message={erro} /><p className="mt-4 text-pfa-slate">Carregando caso…</p></Page>

  return (
    <Page>
      <div className="flex items-center justify-between">
        <div>
          <Link to="/interno" className="text-sm text-pfa-slate hover:text-pfa-navy">← Fila de casos</Link>
          <h1 className="mt-1 text-2xl font-semibold text-pfa-navy">{caso.empreendimento ?? 'Caso'}</h1>
          <p className="text-sm text-pfa-slate">
            {caso.clientes?.nome} · {caso.construtora ?? '—'} · <strong>{statusLabel(caso.status)}</strong>
          </p>
        </div>
      </div>
      <ErrorBox message={erro} />

      <div className="mt-6 grid gap-6 md:grid-cols-2">
        <div className="card">
          <h2 className="font-semibold text-pfa-navy">Billing</h2>
          <p className="mt-2 text-sm text-pfa-slate">Parcela: {brl(caso.valor_parcela)} · Entrada (3×): {brl(caso.valor_entrada)} · Êxito: {caso.percentual_exito ?? 0}%</p>
          <ul className="mt-3 space-y-1 text-sm">
            {pagamentos.map((p) => (
              <li key={p.id} className="flex justify-between border-t border-pfa-navy/10 py-1">
                <span>{p.tipo} · {statusLabel(p.status)}{p.vencimento ? ` · vence ${dataBR(p.vencimento)}` : ''}</span>
                <span className="font-medium">{brl(p.valor)}</span>
              </li>
            ))}
            {pagamentos.length === 0 && <li className="text-pfa-slate/70">Nenhuma cobrança ainda.</li>}
          </ul>
        </div>

        <div className="card">
          <h2 className="font-semibold text-pfa-navy">Documentos</h2>
          <ul className="mt-3 space-y-1 text-sm">
            {docs.map((d) => (
              <li key={d.id} className="flex items-center justify-between gap-3 border-t border-pfa-navy/10 py-1">
                <span className="truncate">
                  {d.tipo}
                  {d.nome_arquivo ? <span className="text-pfa-slate/70"> · {d.nome_arquivo}</span> : null}
                </span>
                <span className="flex shrink-0 items-center gap-3">
                  <span className="text-pfa-slate">{d.status}</span>
                  {d.storage_path && (
                    <button
                      className="font-medium text-pfa-navy underline hover:text-pfa-coral disabled:opacity-50"
                      disabled={docBusy === d.id}
                      onClick={() => abrirDoc(d)}
                    >
                      {docBusy === d.id ? 'abrindo…' : 'Ver'}
                    </button>
                  )}
                </span>
              </li>
            ))}
            {docs.length === 0 && <li className="text-pfa-slate/70">Nenhum documento.</li>}
          </ul>
        </div>
      </div>

      {/* Peça */}
      <div className="card mt-6">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold text-pfa-navy">Petição inicial</h2>
          <button
            className="btn-primary"
            disabled={busy === 'gerar'}
            onClick={() => {
              if (dirty && peticao && !window.confirm('Há edições não salvas na minuta que serão descartadas ao gerar nova versão. Continuar?')) return
              acao('gerar', async () => {
                await apiPost('/api/peticoes/gerar', { caso_id: caso.id, force: Boolean(peticao) }, { auth: true })
              })
            }}
          >
            {busy === 'gerar' ? 'Gerando com Claude…' : peticao ? 'Gerar nova versão' : 'Gerar peça (Claude)'}
          </button>
        </div>

        {peticao ? (
          <div className="mt-4">
            <p className="text-xs text-pfa-slate/70">Versão {peticao.versao} · {statusLabel(peticao.status)} · {peticao.modelo_ia ?? '—'}</p>
            <textarea
              className="input mt-2 h-80 font-mono text-xs"
              value={conteudo}
              onChange={(e) => {
                setConteudo(e.target.value)
                setDirty(true)
              }}
            />
            <div className="mt-3 flex flex-wrap items-end gap-3">
              <button
                className="btn-ghost"
                disabled={busy === 'aprovar'}
                onClick={() => acao('aprovar', async () => {
                  await apiPost('/api/peticoes/aprovar', { peticao_id: peticao.id, acao: 'aprovar', conteudo_md: conteudo }, { auth: true })
                  setDirty(false)
                })}
              >
                Aprovar (revisada)
              </button>
              <div>
                <Field label="Nº do processo (ao protocolar)">
                  <TextInput value={processoNumero} onChange={(e) => setProcessoNumero(e.target.value)} placeholder="opcional" />
                </Field>
              </div>
              <button
                className="btn-accent"
                disabled={busy === 'protocolar'}
                onClick={() => acao('protocolar', async () => {
                  await apiPost('/api/peticoes/aprovar', { peticao_id: peticao.id, acao: 'protocolar', conteudo_md: conteudo, processo_numero: processoNumero || undefined }, { auth: true })
                  setDirty(false)
                })}
              >
                Aprovar e marcar protocolada
              </button>
            </div>
            <p className="mt-2 text-xs text-pfa-slate/70">A minuta é gerada por IA e exige revisão humana de advogado(a) responsável antes do protocolo.</p>
          </div>
        ) : (
          <p className="mt-3 text-sm text-pfa-slate">Nenhuma peça gerada ainda.</p>
        )}
      </div>

      {/* Fatos extraídos pela IA (read-only, SÓ staff — transparência para a revisão da minuta) */}
      {fatos?.fatos && (
        <div className="card mt-6">
          <h2 className="font-semibold text-pfa-navy">Fatos extraídos dos documentos (IA)</h2>
          <p className="mt-1 text-xs text-pfa-slate/70">
            Apoio à minuta — a IA leu os documentos do caso. <strong>Confira na revisão</strong>; dado ausente ou de baixa
            confiança entra como <span className="font-mono">[VERIFICAR]</span> na peça.
            {fatos.extraido_em ? ` · extraído em ${dataBR(fatos.extraido_em)}` : ''}
          </p>
          {fatos.erro && <p className="mt-2 text-xs text-orange-700">Aviso da extração: {fatos.erro}</p>}
          <div className="mt-3 overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="text-xs text-pfa-slate/70">
                  <th className="py-1 pr-3 font-medium">Campo</th>
                  <th className="py-1 pr-3 font-medium">Valor</th>
                  <th className="py-1 pr-3 font-medium">Confiança</th>
                  <th className="py-1 font-medium">Fonte / citação</th>
                </tr>
              </thead>
              <tbody>
                {FATO_LABELS.map(([chave, label]) => {
                  const c = fatos.fatos?.[chave] as CampoFato | undefined
                  return (
                    <tr key={chave} className="border-t border-pfa-navy/10 align-top">
                      <td className="py-1.5 pr-3 text-pfa-slate">{label}</td>
                      <td className="py-1.5 pr-3 font-medium text-pfa-navy">{c?.valor?.trim() ? c.valor : '—'}</td>
                      <td className="py-1.5 pr-3">
                        <span className={`rounded px-1.5 py-0.5 text-xs ${CONF_COR[c?.confianca ?? 'ausente'] ?? CONF_COR.ausente}`}>
                          {c?.confianca ?? 'ausente'}
                        </span>
                      </td>
                      <td className="py-1.5 text-xs text-pfa-slate/80">
                        {c?.documento_fonte?.trim() ? <span className="text-pfa-slate">{c.documento_fonte}</span> : null}
                        {c?.citacao?.trim() ? <span className="block italic">"{c.citacao}"</span> : null}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
          {fatos.fatos.observacoes?.trim() && (
            <p className="mt-3 text-xs text-pfa-slate">
              <strong>Observações da IA:</strong> {fatos.fatos.observacoes}
            </p>
          )}
          {(fatos.docs_ignorados?.length ?? 0) > 0 && (
            <p className="mt-2 text-xs text-pfa-slate/70">
              Não lidos (formato não suportado): {fatos.docs_ignorados!.join(', ')}
            </p>
          )}
        </div>
      )}

      {/* Eventos processuais */}
      <div className="card mt-6">
        <h2 className="font-semibold text-pfa-navy">Eventos processuais</h2>
        <div className="mt-3 flex flex-wrap items-end gap-3">
          <div>
            <label className="label">Tipo</label>
            <select className="input" value={tipoEvento} onChange={(e) => setTipoEvento(e.target.value)}>
              {TIPOS_EVENTO.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div className="flex-1">
            <Field label="Descrição">
              <TextInput value={descEvento} onChange={(e) => setDescEvento(e.target.value)} placeholder="ex.: liminar deferida em ..." />
            </Field>
          </div>
          <button
            className="btn-primary"
            disabled={busy === 'evento'}
            onClick={() => acao('evento', async () => {
              await apiPost('/api/eventos/registrar', { caso_id: caso.id, tipo: tipoEvento, descricao: descEvento || undefined }, { auth: true })
              setDescEvento('')
            })}
          >
            Registrar
          </button>
        </div>
        {tipoEvento === 'liminar_deferida' && (
          <p className="mt-2 text-xs text-pfa-coral">Registrar "liminar_deferida" agenda automaticamente a cobrança da entrada (3× parcela, +5 dias).</p>
        )}
        <ul className="mt-4 space-y-2">
          {eventos.map((ev) => (
            <li key={ev.id} className="border-l-2 border-pfa-coral/40 pl-3 text-sm">
              <span className="text-xs text-pfa-slate/70">{dataBR(ev.data_evento)} · {ev.tipo}</span>
              <p className="text-pfa-navy">{ev.descricao ?? ev.tipo}</p>
            </li>
          ))}
          {eventos.length === 0 && <li className="text-pfa-slate/70 text-sm">Nenhum evento.</li>}
        </ul>
      </div>

      {/* modal de visualização de documento */}
      {viewer && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-pfa-navy/70 p-4"
          onClick={() => setViewer(null)}
        >
          <div
            className="flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-xl bg-white shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between gap-3 border-b border-pfa-navy/10 px-4 py-3">
              <p className="truncate text-sm font-medium text-pfa-navy">{viewer.nome}</p>
              <div className="flex shrink-0 items-center gap-4 text-sm">
                <a
                  href={viewer.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-pfa-navy underline hover:text-pfa-coral"
                >
                  Abrir em nova aba
                </a>
                <button onClick={() => setViewer(null)} className="text-pfa-slate hover:text-pfa-navy" aria-label="Fechar">
                  ✕
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-auto bg-pfa-mist">
              {viewer.kind === 'image' ? (
                <img src={viewer.url} alt={viewer.nome} className="mx-auto block max-h-[80vh]" />
              ) : viewer.kind === 'pdf' ? (
                <iframe src={viewer.url} title={viewer.nome} className="h-[80vh] w-full border-0" />
              ) : (
                <div className="p-10 text-center text-sm text-pfa-slate">
                  Pré-visualização não disponível para este tipo de arquivo.
                  <br />
                  <a href={viewer.url} target="_blank" rel="noopener noreferrer" className="text-pfa-navy underline">
                    Abrir ou baixar o arquivo
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </Page>
  )
}
